import React from 'react';
import * as THREE from 'three';
import type { NPCModelProps, NPCModelRegistry } from '../types';

const Eagle = ({ world, hovered }: NPCModelProps) => {
  const highlightColor = world === 'real' ? '#FFFF00' : '#00FFFF';

  return (
    <group>
      {/* Body */}
      <mesh position={[0, 0.15, 0]} castShadow>
        <capsuleGeometry args={[0.08, 0.15, 8, 16]} />
        <meshStandardMaterial
          color={world === 'real' ? '#4A3728' : '#3a2a2a'}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.3 : 0}
        />
      </mesh>
      {/* Head */}
      <mesh position={[0, 0.32, 0.05]} castShadow>
        <sphereGeometry args={[0.06, 10, 10]} />
        <meshStandardMaterial color={world === 'real' ? '#FFFFFF' : '#e0e0e8'} />
      </mesh>
      {/* Beak */}
      <mesh position={[0, 0.3, 0.11]} rotation={[0.4, 0, 0]}>
        <coneGeometry args={[0.02, 0.06, 4]} />
        <meshStandardMaterial color={world === 'real' ? '#FFD700' : '#b0a030'} />
      </mesh>
      {/* Wings - spread */}
      {[-1, 1].map((side, i) => (
        <mesh key={i} position={[side * 0.25, 0.18, 0]} rotation={[0.1, 0, side * 0.3]}>
          <planeGeometry args={[0.35, 0.15]} />
          <meshStandardMaterial
            color={world === 'real' ? '#4A3728' : '#3a2a2a'}
            side={THREE.DoubleSide}
          />
        </mesh>
      ))}
      {/* Tail feathers */}
      <mesh position={[0, 0.1, -0.15]} rotation={[0.3, 0, 0]}>
        <planeGeometry args={[0.12, 0.15]} />
        <meshStandardMaterial
          color={world === 'real' ? '#4A3728' : '#3a2a2a'}
          side={THREE.DoubleSide}
        />
      </mesh>
      {/* Eyes */}
      {[-0.025, 0.025].map((x, i) => (
        <mesh key={i} position={[x, 0.33, 0.09]}>
          <sphereGeometry args={[0.012, 8, 8]} />
          <meshBasicMaterial color="#FFD700" />
        </mesh>
      ))}
    </group>
  );
};

const Goat = ({ world, hovered }: NPCModelProps) => {
  const highlightColor = world === 'real' ? '#FFFF00' : '#00FFFF';

  return (
    <group>
      {/* Body */}
      <mesh position={[0, 0.2, 0]} castShadow>
        <boxGeometry args={[0.2, 0.18, 0.35]} />
        <meshStandardMaterial
          color={world === 'real' ? '#D3D3D3' : '#a0a0a8'}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.3 : 0}
        />
      </mesh>
      {/* Head */}
      <mesh position={[0, 0.32, 0.2]} castShadow>
        <boxGeometry args={[0.1, 0.1, 0.12]} />
        <meshStandardMaterial color={world === 'real' ? '#D3D3D3' : '#a0a0a8'} />
      </mesh>
      {/* Horns */}
      {[-0.05, 0.05].map((x, i) => (
        <mesh key={i} position={[x, 0.4, 0.18]} rotation={[0.5, 0, (i - 0.5) * 0.6]}>
          <coneGeometry args={[0.02, 0.12, 6]} />
          <meshStandardMaterial color={world === 'real' ? '#3a3a3a' : '#2a2a2a'} />
        </mesh>
      ))}
      {/* Beard */}
      <mesh position={[0, 0.25, 0.26]}>
        <coneGeometry args={[0.02, 0.06, 4]} />
        <meshStandardMaterial color={world === 'real' ? '#B8B8B8' : '#909098'} />
      </mesh>
      {/* Legs */}
      {[[-0.07, 0.12], [0.07, 0.12], [-0.07, -0.12], [0.07, -0.12]].map(([x, z], i) => (
        <mesh key={i} position={[x, 0.08, z]}>
          <cylinderGeometry args={[0.025, 0.03, 0.16, 6]} />
          <meshStandardMaterial color={world === 'real' ? '#D3D3D3' : '#a0a0a8'} />
        </mesh>
      ))}
      {/* Ears */}
      {[-0.06, 0.06].map((x, i) => (
        <mesh key={i} position={[x, 0.35, 0.15]} rotation={[0, 0, (i - 0.5) * 0.8]}>
          <planeGeometry args={[0.04, 0.03]} />
          <meshStandardMaterial color={world === 'real' ? '#D3D3D3' : '#a0a0a8'} side={THREE.DoubleSide} />
        </mesh>
      ))}
      {/* Eyes */}
      {[-0.03, 0.03].map((x, i) => (
        <mesh key={i} position={[x, 0.34, 0.25]}>
          <sphereGeometry args={[0.012, 8, 8]} />
          <meshBasicMaterial color="#FFD700" />
        </mesh>
      ))}
    </group>
  );
};

const Wolf = ({ world, hovered }: NPCModelProps) => {
  const highlightColor = world === 'real' ? '#FFFF00' : '#00FFFF';

  return (
    <group>
      {/* Body - sitting */}
      <mesh position={[0, 0.18, 0]} rotation={[-0.2, 0, 0]} castShadow>
        <capsuleGeometry args={[0.1, 0.18, 8, 16]} />
        <meshStandardMaterial
          color={world === 'real' ? '#696969' : '#4a4a5a'}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.3 : 0}
        />
      </mesh>
      {/* Head */}
      <mesh position={[0, 0.38, 0.08]} castShadow>
        <boxGeometry args={[0.12, 0.1, 0.14]} />
        <meshStandardMaterial color={world === 'real' ? '#696969' : '#4a4a5a'} />
      </mesh>
      {/* Snout */}
      <mesh position={[0, 0.35, 0.18]}>
        <boxGeometry args={[0.06, 0.05, 0.08]} />
        <meshStandardMaterial color={world === 'real' ? '#808080' : '#5a5a6a'} />
      </mesh>
      {/* Nose */}
      <mesh position={[0, 0.36, 0.22]}>
        <sphereGeometry args={[0.02, 6, 6]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>
      {/* Ears - pointed */}
      {[-0.05, 0.05].map((x, i) => (
        <mesh key={i} position={[x, 0.47, 0.05]} rotation={[0.2, 0, (i - 0.5) * 0.2]}>
          <coneGeometry args={[0.03, 0.08, 4]} />
          <meshStandardMaterial color={world === 'real' ? '#696969' : '#4a4a5a'} />
        </mesh>
      ))}
      {/* Front legs */}
      {[-0.06, 0.06].map((x, i) => (
        <mesh key={i} position={[x, 0.08, 0.1]}>
          <cylinderGeometry args={[0.03, 0.035, 0.16, 6]} />
          <meshStandardMaterial color={world === 'real' ? '#696969' : '#4a4a5a'} />
        </mesh>
      ))}
      {/* Tail */}
      <mesh position={[0, 0.15, -0.15]} rotation={[0.8, 0, 0]}>
        <capsuleGeometry args={[0.03, 0.12, 4, 8]} />
        <meshStandardMaterial color={world === 'real' ? '#696969' : '#4a4a5a'} />
      </mesh>
      {/* Eyes */}
      {[-0.035, 0.035].map((x, i) => (
        <mesh key={i} position={[x, 0.4, 0.14]}>
          <sphereGeometry args={[0.015, 8, 8]} />
          <meshBasicMaterial color={world === 'real' ? '#FFD700' : '#B0C4DE'} />
        </mesh>
      ))}
    </group>
  );
};

const Marmot = ({ world, hovered }: NPCModelProps) => {
  const highlightColor = world === 'real' ? '#FFFF00' : '#00FFFF';

  return (
    <group>
      {/* Body - standing alert */}
      <mesh position={[0, 0.12, 0]} castShadow>
        <capsuleGeometry args={[0.08, 0.1, 8, 16]} />
        <meshStandardMaterial
          color={world === 'real' ? '#8B7355' : '#6a5a4a'}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.3 : 0}
        />
      </mesh>
      {/* Head */}
      <mesh position={[0, 0.25, 0.03]} castShadow>
        <sphereGeometry args={[0.06, 10, 10]} />
        <meshStandardMaterial color={world === 'real' ? '#8B7355' : '#6a5a4a'} />
      </mesh>
      {/* Snout */}
      <mesh position={[0, 0.23, 0.08]}>
        <sphereGeometry args={[0.03, 8, 8]} />
        <meshStandardMaterial color={world === 'real' ? '#A0896E' : '#7a6a5a'} />
      </mesh>
      {/* Nose */}
      <mesh position={[0, 0.23, 0.11]}>
        <sphereGeometry args={[0.012, 6, 6]} />
        <meshStandardMaterial color="#2a2a2a" />
      </mesh>
      {/* Ears */}
      {[-0.04, 0.04].map((x, i) => (
        <mesh key={i} position={[x, 0.3, 0]}>
          <sphereGeometry args={[0.02, 6, 6]} />
          <meshStandardMaterial color={world === 'real' ? '#8B7355' : '#6a5a4a'} />
        </mesh>
      ))}
      {/* Front paws - held up */}
      {[-0.05, 0.05].map((x, i) => (
        <mesh key={i} position={[x, 0.12, 0.06]}>
          <capsuleGeometry args={[0.02, 0.04, 4, 6]} />
          <meshStandardMaterial color={world === 'real' ? '#8B7355' : '#6a5a4a'} />
        </mesh>
      ))}
      {/* Eyes */}
      {[-0.025, 0.025].map((x, i) => (
        <mesh key={i} position={[x, 0.27, 0.06]}>
          <sphereGeometry args={[0.012, 8, 8]} />
          <meshBasicMaterial color="#2a2a2a" />
        </mesh>
      ))}
    </group>
  );
};

const Owl = ({ world, hovered }: NPCModelProps) => {
  const highlightColor = world === 'real' ? '#FFFF00' : '#00FFFF';

  return (
    <group>
      {/* Body */}
      <mesh position={[0, 0.15, 0]} castShadow>
        <capsuleGeometry args={[0.08, 0.12, 8, 16]} />
        <meshStandardMaterial
          color={world === 'real' ? '#8B7355' : '#5a4a4a'}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.3 : 0}
        />
      </mesh>
      {/* Head - large and round */}
      <mesh position={[0, 0.32, 0]} castShadow>
        <sphereGeometry args={[0.08, 12, 12]} />
        <meshStandardMaterial color={world === 'real' ? '#A0896E' : '#6a5a5a'} />
      </mesh>
      {/* Face disc */}
      <mesh position={[0, 0.32, 0.05]}>
        <circleGeometry args={[0.06, 16]} />
        <meshStandardMaterial color={world === 'real' ? '#DEB887' : '#b0a090'} />
      </mesh>
      {/* HUGE eyes */}
      {[-0.025, 0.025].map((x, i) => (
        <group key={i} position={[x, 0.34, 0.06]}>
          <mesh>
            <sphereGeometry args={[0.025, 10, 10]} />
            <meshBasicMaterial color="#FFD700" />
          </mesh>
          <mesh position={[0, 0, 0.015]}>
            <sphereGeometry args={[0.012, 8, 8]} />
            <meshBasicMaterial color="#000000" />
          </mesh>
        </group>
      ))}
      {/* Beak */}
      <mesh position={[0, 0.29, 0.08]} rotation={[0.5, 0, 0]}>
        <coneGeometry args={[0.015, 0.03, 4]} />
        <meshStandardMaterial color={world === 'real' ? '#4a4a4a' : '#3a3a3a'} />
      </mesh>
      {/* Ear tufts */}
      {[-0.05, 0.05].map((x, i) => (
        <mesh key={i} position={[x, 0.42, -0.02]} rotation={[0.3, 0, (i - 0.5) * 0.4]}>
          <coneGeometry args={[0.015, 0.05, 4]} />
          <meshStandardMaterial color={world === 'real' ? '#8B7355' : '#5a4a4a'} />
        </mesh>
      ))}
      {/* Wings folded */}
      {[-0.08, 0.08].map((x, i) => (
        <mesh key={i} position={[x, 0.15, -0.02]}>
          <planeGeometry args={[0.08, 0.15]} />
          <meshStandardMaterial
            color={world === 'real' ? '#6B5344' : '#4a3a3a'}
            side={THREE.DoubleSide}
          />
        </mesh>
      ))}
    </group>
  );
};

export const mountainAnimals: NPCModelRegistry = {
  eagle: Eagle,
  goat: Goat,
  wolf: Wolf,
  marmot: Marmot,
  owl: Owl,
};
